/**
 * Cheques.
 *
 * QUE PREGUNTA CONTESTA ESTA PANTALLA
 * -----------------------------------
 * "Que cheques tengo, cuanto valen, y cual tengo que ir a cobrar YA."
 *
 * Lo primero que se ve arriba no es la lista: es la alerta de los que ya
 * vencieron y siguen en cartera. Un cheque vencido sin depositar es plata que
 * esta ahi y nadie fue a buscar, y es lo unico de esta pantalla que pide una
 * accion hoy.
 *
 * Los cheques pueden ser en pesos o en dolares, asi que no hay un total: hay uno
 * por moneda. Y depositar exige que la moneda del cheque coincida con la de la
 * cuenta, por eso el desplegable de destino solo ofrece las que pueden
 * recibirlo.
 */
import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Clock, Landmark, TriangleAlert, type LucideIcon } from "lucide-react";
import { formatear } from "@tora/shared";

import { PageHeader } from "@/components/layout/PageHeader";
import { Pagina } from "@/components/layout/Pagina";
import { Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Segmented } from "@/components/ui/segmented";
import {
  DialogAcreditar,
  DialogDepositar,
  DialogEndosar,
  DialogRechazar,
} from "@/components/caja/DialogsCheque";
import { ListaCheques } from "@/components/caja/ListaCheques";
import { totalizarPorMoneda, urgenciaCheque, type ChequeCartera } from "@/components/caja/logica";
import {
  useCheques,
  useCuentasFinancieras,
  useAcreditarCheque,
  useDepositarCheque,
  useEndosarCheque,
  useProveedores,
  useRechazarCheque,
} from "@/lib/queries";
import { mostrarError, mostrarExito } from "@/lib/errores";
import { aNumero } from "@/lib/tipos";

export const Route = createFileRoute("/cheques")({ component: Cheques });

/**
 * Las vistas de la pantalla.
 *
 * "Cartera" (sin filtro de estado) es la vista `v_cheques_cartera` del backend:
 * los que siguen siendo nuestros, o sea EN_CARTERA mas DEPOSITADO. El resto de
 * las solapas piden un estado puntual y traen de la tabla cruda. La division no
 * es cosmetica: es exactamente como parte los datos el endpoint.
 */
const VISTAS: ReadonlyArray<{ clave: string; etiqueta: string; estado?: string }> = [
  { clave: "cartera", etiqueta: "Cartera" },
  { clave: "COBRADO", etiqueta: "Cobrados", estado: "COBRADO" },
  { clave: "ENTREGADO", etiqueta: "Entregados", estado: "ENTREGADO" },
  { clave: "RECHAZADO", etiqueta: "Rechazados", estado: "RECHAZADO" },
];

function Cheques(): JSX.Element {
  const [vista, setVista] = useState<string>("cartera");
  const [aDepositar, setADepositar] = useState<ChequeCartera | null>(null);
  const [aRechazar, setARechazar] = useState<ChequeCartera | null>(null);
  const [aEndosar, setAEndosar] = useState<ChequeCartera | null>(null);
  const [aAcreditar, setAAcreditar] = useState<ChequeCartera | null>(null);

  const estado = VISTAS.find((v) => v.clave === vista)?.estado;
  const { data, isFetching } = useCheques(estado);
  const { data: cuentas } = useCuentasFinancieras();

  const depositar = useDepositarCheque();
  const rechazar = useRechazarCheque();
  const endosar = useEndosarCheque();
  const acreditar = useAcreditarCheque();
  const { data: proveedores } = useProveedores();

  const listaCuentas = useMemo(() => cuentas?.filas ?? [], [cuentas]);
  const cheques = useMemo(() => (data?.filas ?? []) as ChequeCartera[], [data]);

  // La alerta se calcula sobre lo que hay en pantalla. Solo miran los que siguen
  // EN_CARTERA: un cheque ya depositado no "vence" para nosotros.
  const { vencidos, porVencer } = useMemo(() => {
    const hoy = new Date();
    const v: ChequeCartera[] = [];
    const p: ChequeCartera[] = [];
    for (const ch of cheques) {
      const { urgencia } = urgenciaCheque(ch, hoy);
      if (urgencia === "VENCIDO") v.push(ch);
      else if (urgencia === "POR_VENCER") p.push(ch);
    }
    return { vencidos: v, porVencer: p };
  }, [cheques]);

  return (
    <Pagina>
      <PageHeader titulo="Cheques" icono={Landmark} />

      {vencidos.length > 0 ? (
        <Alerta
          tono="destructive"
          icono={TriangleAlert}
          titulo={`${vencidos.length} ${vencidos.length === 1 ? "cheque venció" : "cheques vencieron"} y sigue${vencidos.length === 1 ? "" : "n"} en cartera`}
          detalle="La fecha de cobro ya pasó y el cheque no se depositó. Es plata que está esperando que alguien vaya a buscarla."
          cheques={vencidos}
        />
      ) : null}

      {porVencer.length > 0 ? (
        <Alerta
          tono="warning"
          icono={Clock}
          titulo={`${porVencer.length} ${porVencer.length === 1 ? "cheque se cobra" : "cheques se cobran"} esta semana`}
          detalle="Dentro de los próximos 7 días. Conviene tenerlos a mano."
          cheques={porVencer}
        />
      ) : null}

      <Segmented
        etiqueta="Estado de los cheques"
        apariencia="chips"
        tamano="sm"
        opciones={VISTAS.map((v) => ({ valor: v.clave, etiqueta: v.etiqueta }))}
        valor={vista}
        onChange={setVista}
      />

      {vista === "cartera" ? (
        <p className="text-xs text-muted-foreground">
          La cartera son los cheques que todavía son nuestros: los que están guardados y los que ya
          se depositaron pero no se acreditaron.
        </p>
      ) : null}

      <ListaCheques
        cheques={cheques}
        onDepositar={setADepositar}
        onRechazar={setARechazar}
        onEndosar={setAEndosar}
        onAcreditar={setAAcreditar}
        cargando={isFetching}
        mensajeVacio="No hay cheques en este estado."
      />

      <DialogDepositar
        cheque={aDepositar}
        onCerrar={() => setADepositar(null)}
        cuentas={listaCuentas}
        guardando={depositar.isPending}
        onConfirmar={(datos) =>
          depositar.mutate(datos, {
            onSuccess: () => {
              const ch = aDepositar;
              setADepositar(null);
              const cuenta = listaCuentas.find((c) => c.id === datos.cuenta_id);
              mostrarExito(
                ch ? `Cheque ${ch.numero} depositado` : "Cheque depositado",
                ch && cuenta
                  ? `${formatear(aNumero(ch.importe), ch.moneda)} en ${cuenta.nombre}`
                  : undefined,
              );
            },
            onError: mostrarError,
          })
        }
      />

      <DialogEndosar
        key={aEndosar?.id ?? "cerrado"}
        cheque={aEndosar}
        proveedores={proveedores?.filas ?? []}
        onCerrar={() => setAEndosar(null)}
        guardando={endosar.isPending}
        onConfirmar={(datos) =>
          endosar.mutate(datos, {
            onSuccess: () => {
              const ch = aEndosar;
              setAEndosar(null);
              const prov = proveedores?.filas.find((p) => p.proveedor_id === datos.proveedor_id);
              mostrarExito(
                ch ? `Cheque ${ch.numero} endosado` : "Cheque endosado",
                prov ? `Entregado a ${prov.nombre}: bajó lo que se le debe.` : undefined,
              );
            },
            onError: mostrarError,
          })
        }
      />

      <DialogAcreditar
        cheque={aAcreditar}
        onCerrar={() => setAAcreditar(null)}
        guardando={acreditar.isPending}
        onConfirmar={(datos) =>
          acreditar.mutate(datos, {
            onSuccess: () => {
              const ch = aAcreditar;
              setAAcreditar(null);
              mostrarExito(
                ch ? `Cheque ${ch.numero} acreditado` : "Cheque acreditado",
                "Pasó a Cobrados.",
              );
            },
            onError: mostrarError,
          })
        }
      />

      <DialogRechazar
        cheque={aRechazar}
        onCerrar={() => setARechazar(null)}
        guardando={rechazar.isPending}
        onConfirmar={(datos) =>
          rechazar.mutate(datos, {
            onSuccess: () => {
              const ch = aRechazar;
              setARechazar(null);
              mostrarExito(
                ch ? `Cheque ${ch.numero} rechazado` : "Cheque rechazado",
                "La deuda volvió a la cuenta corriente del cliente.",
              );
            },
            onError: mostrarError,
          })
        }
      />
    </Pagina>
  );
}

/**
 * La alerta de arriba: cuantos cheques piden atencion y cuanta plata suman,
 * por moneda. El tono lo llevan el borde y el icono; el texto sigue siendo
 * `foreground`, y cada total va en el color de SU moneda.
 */
function Alerta({
  tono,
  icono,
  titulo,
  detalle,
  cheques,
}: {
  tono: "destructive" | "warning";
  icono: LucideIcon;
  titulo: string;
  detalle: string;
  cheques: ReadonlyArray<ChequeCartera>;
}): JSX.Element {
  const totales = totalizarPorMoneda(cheques);
  return (
    <Aviso tono={tono} icono={icono} titulo={titulo}>
      <p className="text-muted-foreground">{detalle}</p>
      <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
        {totales.cantidadArs > 0 ? <Importe monto={totales.ars} moneda="ARS" /> : null}
        {totales.cantidadUsd > 0 ? <Importe monto={totales.usd} moneda="USD" /> : null}
      </p>
    </Aviso>
  );
}
